import { DEFAULT_CONSTRAINTS, DEFAULT_OPTIONS, Recorder, WebcamStatus } from '@domain/constant/webcamTypes';
import WebcamUtil from '@utils/WebcamUtil';

// 녹화 관련 class
class RecorderService {
  recorder: Recorder | null = null;
  lastRecordingData: Blob | null = null;
  status: WebcamStatus = WebcamStatus.UNINITIALIZED;
  private _recordingOrWebcamOrMicError = false;
  reconnected = false;
  private _cameraPerm: PermissionStatus | null = null;
  private _micPerm: PermissionStatus | null = null;

  constructor(private readonly pauseTtsAudio: () => void) {}

  readonly setRecordingOrWebcamOrMicError = (value: boolean) => {
    if (value) {
      this.pauseTtsAudio();
    }
    this.recordingOrWebcamOrMicError = value;
  };

  async initialize(options = DEFAULT_OPTIONS, streamConstraints: MediaStreamConstraints = DEFAULT_CONSTRAINTS) {
    try {
      this.recorder = await WebcamUtil.getRecorderPromise(options, streamConstraints);
      this.status = WebcamStatus.INACTIVE;

      // From ACC
      // 아래 코드는 마이크나 카메라 끊김을 체크하기 위함입니다
      const setRecordingOrWebcamOrMicError = this.setRecordingOrWebcamOrMicError;
      this._cameraPerm = await navigator.permissions.query({ name: 'camera' } as unknown as PermissionDescriptor);
      this._cameraPerm.onchange = (event) => {
        if ((event.currentTarget as PermissionStatus).state === 'denied') {
          setRecordingOrWebcamOrMicError(true);
        }
      };

      this._micPerm = await navigator.permissions.query({ name: 'microphone' } as unknown as PermissionDescriptor);
      this._micPerm.onchange = (event) => {
        if ((event.currentTarget as PermissionStatus).state === 'denied') {
          setRecordingOrWebcamOrMicError(true);
        }
      };
      if (this.recorder.stream.getVideoTracks().length > 0) {
        this.recorder.stream.getVideoTracks()[0].onended = () => {
          //it can be fired when audio permission denied
          setRecordingOrWebcamOrMicError(true);
        };
      }
      if (this.recorder.stream.getAudioTracks().length > 0) {
        this.recorder.stream.getAudioTracks()[0].onended = () => {
          setRecordingOrWebcamOrMicError(true);
        };
      }
    } catch (e: any) {
      try {
        if (!this.reconnected) {
          this.reconnected = true;
          await this.reconnect();
        }
      } catch (e: any) {
        console.error(e);
        this.recordingOrWebcamOrMicError = true;
      }
      console.error(e);
      this.recordingOrWebcamOrMicError = true;
    }
  }

  async startRecord() {
    if (this.recorder) {
      try {
        this.status = WebcamStatus.ACTIVE;
        await this.recorder.startRecording();
      } catch (e: any) {
        this.status = WebcamStatus.ERROR;
        console.error(e);
        this.recordingOrWebcamOrMicError = true;
      }
    } else {
      this.recordingOrWebcamOrMicError = true;
    }
  }

  async stopRecord() {
    if (this.recorder) {
      if (this.status !== WebcamStatus.ACTIVE) {
        this.recordingOrWebcamOrMicError = true;
      }
      try {
        this.status = WebcamStatus.INACTIVE;
        await this.recorder.stopRecording();
        this.lastRecordingData = await this.recorder.getBlob();
      } catch (e: any) {
        this.status = WebcamStatus.ERROR;
        console.error(e);
        this.recordingOrWebcamOrMicError = true;
      }
    } else {
      this.recordingOrWebcamOrMicError = true;
    }
  }

  async reconnect() {
    this.recorder = null;
    this.lastRecordingData = null;
    this.status = WebcamStatus.UNINITIALIZED;
    await this.initialize();
  }

  get recordingOrWebcamOrMicError(): boolean {
    return this._recordingOrWebcamOrMicError;
  }

  set recordingOrWebcamOrMicError(value: boolean) {
    if (value) {
      this.pauseTtsAudio();
    }
    this._recordingOrWebcamOrMicError = value;
  }
}

export default RecorderService;
